import { defineColumns } from './piColumns.js';
import { EMPTY, formatDateFull, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { cohortLabel, cohortRows, offsetLabel, sequentialStyle } from './piScale.js';
import { listOf } from './piRequests.js';
import { paddedKey } from './piTable.js';

export const RETENTION_TYPES = [
  { value: 'user', label: 'Usuários' },
  { value: 'company', label: 'Empresas' },
];

export const RETENTION_GRANULARITIES = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
];

export const DEFAULT_RETENTION_TYPE = 'user';

export const DEFAULT_RETENTION_GRANULARITY = 'week';

export const COMPANY_RETENTION_GRANULARITY = 'month';

export const COMPANY_GRANULARITY_TEXT = 'A retenção de empresas é calculada só por mês (coorte = mês da ativação); ' +
  'Dia e Semana valem só para usuários e voltam ao escolher Usuários.';

export const KEY_OFFSETS = { day: [1, 7, 30], week: [1, 4, 8, 12], month: [1, 3, 6, 12] };

export const CURVE_LIMIT = 6;

const numbersOf = (values) => (Array.isArray(values) ? values.filter((value) => isNumber(value)) : []);

const inValues = (options, value) => options.some((option) => option.value === value);

export const granularityAllowed = (type, granularity) => type !== 'company' || granularity === COMPANY_RETENTION_GRANULARITY;

export const retentionChoice = (type, granularity, userGranularity) => {
  const kind = inValues(RETENTION_TYPES, type) ? type : DEFAULT_RETENTION_TYPE;
  const candidates = kind === 'company' ? [userGranularity, granularity] : [granularity, userGranularity];
  const remembered = candidates.filter((value) => inValues(RETENTION_GRANULARITIES, value))[0] || DEFAULT_RETENTION_GRANULARITY;
  return {
    type: kind,
    granularity: kind === 'company' ? COMPANY_RETENTION_GRANULARITY : remembered,
    userGranularity: remembered,
  };
};

export const chooseRetentionType = (choice, type) => {
  const current = choice || retentionChoice();
  return retentionChoice(type, current.userGranularity, current.userGranularity);
};

export const chooseRetentionGranularity = (choice, granularity) => {
  const current = choice || retentionChoice();
  if (!granularityAllowed(current.type, granularity)) return retentionChoice(current.type, current.granularity, current.userGranularity);
  return retentionChoice(current.type, granularity, current.type === 'company' ? current.userGranularity : granularity);
};

export const retentionInfoKey = (type) => (type === 'company' ? 'retention.companyRetention' : 'retention.userRetention');

export const offsetsOf = (data) => {
  const source = data && typeof data === 'object' ? data : {};
  const found = [];
  const add = (value) => {
    const number = Number(value);
    if (isNumber(number) && number >= 0 && Math.floor(number) === number && found.indexOf(number) === -1) found.push(number);
  };
  if (Array.isArray(source.offsets) && source.offsets.length) source.offsets.forEach(add);
  else listOf(source.cohorts).forEach((cohort) => listOf(cohort.cells).forEach((cell) => add(cell.offset)));
  return found.sort((a, b) => a - b);
};

const keyText = (cohort) => (cohort.cohortKey === null || cohort.cohortKey === undefined ? '' : String(cohort.cohortKey));

export const sortedCohorts = (cohorts) => listOf(cohorts)
  .slice()
  .sort((a, b) => {
    const left = keyText(a);
    const right = keyText(b);
    return left < right ? -1 : left > right ? 1 : 0;
  });

const cellsByOffset = (cohort) => {
  const map = {};
  listOf(cohort.cells).forEach((cell) => {
    if (isNumber(cell.offset)) map[cell.offset] = cell;
  });
  return map;
};

export const retentionCurves = (cohorts, offsets, limit) => {
  const wanted = isNumber(limit) && limit > 0 ? limit : CURVE_LIMIT;
  const sorted = sortedCohorts(cohorts);
  const recent = sorted.slice(Math.max(0, sorted.length - wanted));
  return recent.map((cohort) => {
    const cells = cellsByOffset(cohort);
    return {
      key: keyText(cohort),
      label: cohortLabel(cohort.cohortKey) + (isNumber(cohort.size) ? ' (' + formatInteger(cohort.size) + ')' : ''),
      points: numbersOf(offsets).map((offset) => {
        const cell = cells[offset];
        return {
          date: paddedKey(offset),
          value: cell && cell.matured === true && isNumber(cell.pct) ? cell.pct : null,
        };
      }),
    };
  });
};

export const offsetLabelOf = (granularity) => (key) => offsetLabel(Number(key), granularity);

export const weightedRetention = (cohorts, offsets) => numbersOf(offsets).map((offset) => {
  let retained = 0;
  let size = 0;
  let count = 0;
  listOf(cohorts).forEach((cohort) => {
    const cell = cellsByOffset(cohort)[offset];
    if (!cell || cell.matured !== true || !isNumber(cohort.size) || cohort.size <= 0) return;
    let kept = null;
    if (isNumber(cell.retainedCumulative)) kept = cell.retainedCumulative;
    else if (isNumber(cell.pct)) kept = (cell.pct / 100) * cohort.size;
    if (kept === null) return;
    retained += kept;
    size += cohort.size;
    count += 1;
  });
  const pct = size > 0 ? (retained / size) * 100 : null;
  const style = sequentialStyle(pct);
  return {
    offset: offset,
    pct: pct,
    cohorts: count,
    text: pct === null ? 'n/d' : formatPercent(pct),
    title: pct === null
      ? 'Nenhuma coorte madura neste período.'
      : formatPercent(pct) + ' · média ponderada pelo tamanho de ' + count + (count === 1 ? ' coorte madura' : ' coortes maduras'),
    style: pct === null ? null : { background: style.background, color: style.color },
  };
});

export const retentionComparison = (cohorts, granularity) => {
  const offsets = KEY_OFFSETS[granularity] || KEY_OFFSETS.week;
  const sorted = sortedCohorts(cohorts).reverse();
  return {
    offsets: offsets,
    headers: offsets.map((offset) => offsetLabel(offset, granularity)),
    rows: cohortRows(sorted, offsets),
    average: weightedRetention(sorted, offsets),
  };
};

export const retentionSummary = (cohorts) => {
  const list = listOf(cohorts);
  const total = list.reduce((sum, cohort) => sum + (isNumber(cohort.size) ? cohort.size : 0), 0);
  const derived = list.filter((cohort) => cohort.source === 'B' || cohort.source === 'D').length;
  return {
    cohortsText: formatInteger(list.length) + (list.length === 1 ? ' coorte' : ' coortes'),
    sizeText: list.length ? formatInteger(total) : EMPTY,
    derivedText: derived ? derived + (derived === 1 ? ' coorte retroativa (derivada)' : ' coortes retroativas (derivadas)') : '',
  };
};

export const AVERAGE_LABEL = 'Média ponderada (coortes maduras)';

const OFFSET_LIMITS = { day: 30, week: 12, month: 12 };

const OFFSET_KEY = /^[dwm]\d+$/;

const offsetKey = (granularity, offset) => offsetLabel(offset, granularity).toLowerCase();

const maturedPct = (row, offset) => {
  const cell = row && row.cells ? row.cells[offset] : null;
  return cell && cell.matured === true && isNumber(cell.pct) ? cell.pct : null;
};

const pendingFrom = (row) => {
  const cells = row && row.cells ? row.cells : {};
  const pending = Object.keys(cells)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((offset) => cells[offset].matured !== true)[0];
  if (pending === undefined) return '';
  const date = formatDateFull(cells[pending].maturityDate);
  return offsetLabel(pending, row.granularity) + (date === EMPTY ? '' : ' (sai em ' + date + ')');
};

const offsetColumn = (granularity, offset) => ({
  key: offsetKey(granularity, offset),
  label: offsetLabel(offset, granularity),
  type: 'percent',
  infoKey: 'retention.offset',
  value: (row) => maturedPct(row, offset),
});

const offsetRange = (granularity) => {
  const list = [];
  for (let offset = 0; offset <= OFFSET_LIMITS[granularity]; offset += 1) list.push(offsetColumn(granularity, offset));
  return list;
};

const keyOffsetColumns = (granularity) => KEY_OFFSETS[granularity].map((offset) => offsetColumn(granularity, offset));

const sizeColumns = () => [
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'retention.cohortSize', value: (row) => row.size },
  { key: 'companySize', label: 'Empresas', type: 'int', infoKey: 'retention.cohortSize', value: (row) => row.size },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'retention.companies', screen: false },
  { key: 'derived', label: 'Retroativa', type: 'text', infoKey: 'retention.derived', screen: false },
];

const pendingColumn = () => ({ key: 'pendingFrom', label: 'Primeira célula pendente', type: 'text', infoKey: 'retention.pending', screen: false, value: pendingFrom });

export const RETENTION_MATRIX_COLUMNS = defineColumns(
  [{ key: 'cohort', label: 'Coorte', type: 'date', infoKey: 'retention.cohort', value: (row) => row.cohortKey }]
    .concat(sizeColumns(), offsetRange('day'), offsetRange('week'), offsetRange('month'), [pendingColumn()])
);

export const RETENTION_COMPARISON_COLUMNS = defineColumns(
  [{ key: 'cohort', label: 'Coorte', type: 'text', infoKey: 'retention.cohort', value: (row) => row.label }]
    .concat(sizeColumns(), keyOffsetColumns('day'), keyOffsetColumns('week'), keyOffsetColumns('month'), [pendingColumn()])
);

export const retentionColumns = (specs, type, granularity, offsets) => {
  const hidden = type === 'company' ? ['users', 'companies'] : ['companySize'];
  const wanted = numbersOf(offsets).map((offset) => offsetKey(granularity, offset));
  return (Array.isArray(specs) ? specs : []).filter((column) => hidden.indexOf(column.key) === -1
    && (!OFFSET_KEY.test(column.key) || wanted.indexOf(column.key) !== -1));
};

export const columnOffsets = (columns) => (Array.isArray(columns) ? columns : [])
  .filter((column) => OFFSET_KEY.test(column.key))
  .map((column) => Number(column.key.slice(1)));

const tableRow = (cohort, granularity) => ({
  key: keyText(cohort),
  cohortKey: keyText(cohort),
  label: cohortLabel(cohort.cohortKey),
  size: isNumber(cohort.size) ? cohort.size : null,
  companies: isNumber(cohort.companies) ? cohort.companies : null,
  derived: cohort.source === 'B' || cohort.source === 'D',
  granularity: granularity,
  cells: cellsByOffset(cohort),
});

export const retentionMatrixRows = (cohorts, granularity) => listOf(cohorts).map((cohort) => tableRow(cohort, granularity));

export const retentionComparisonRows = (cohorts, granularity) => {
  const offsets = KEY_OFFSETS[granularity] || KEY_OFFSETS.week;
  const sorted = sortedCohorts(cohorts).reverse();
  const cells = {};
  weightedRetention(sorted, offsets).forEach((item) => {
    cells[item.offset] = { offset: item.offset, matured: true, pct: item.pct };
  });
  const average = { key: 'average', cohortKey: '', label: AVERAGE_LABEL, size: null, companies: null, derived: null, granularity: granularity, cells: cells };
  return [average].concat(sorted.map((cohort) => tableRow(cohort, granularity)));
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piRetentionModel.js